import { GuidanceManager } from '@carboncopyco/editor-ui';
import {
  compareUrls,
  getDecodedUrlToDisplay,
} from '@carboncopyco/editor-utils';
import type {
  GuidanceTooltipSchemaType,
  Modification,
} from '@carboncopyco/types';
import { memo } from 'react';
import { useEditorTools } from '../context';
import {
  useEditor,
  useEditorGuidanceTooltips,
  useEditorMetadata,
} from '../context/EditorSdkProvider';
import { useEditorTool } from '../hooks/useEditorTool';

export const GuidanceTool = memo(() => {
  const { isOpen, close } = useEditorTool('guidanceManager');
  const {
    guidanceTooltipsModifications,
    startGuidanceTooltipsMode,
    deleteGuidanceTooltipModification,
    toggleGuidanceTooltipModificationVisibility,
    highlightGuidanceTooltip,
    destroyHighlightGuidanceTooltip,
    navigateToGuidanceTooltip,
  } = useEditorGuidanceTooltips();

  const editor = useEditor();
  const { close: closeModal } = useEditorTools();

  const { location } = useEditorMetadata();
  const pageUrl = getDecodedUrlToDisplay(location?.decodedUrl) ?? '';

  const handleOnClick = (
    modification: Modification<GuidanceTooltipSchemaType.guidanceTooltip>
  ) => {
    const isCurrentPage = compareUrls(pageUrl, modification.content.url);
    closeModal('guidanceManager');
    if (isCurrentPage) {
      navigateToGuidanceTooltip(modification);
      return;
    }
    editor.setNextEditorLoadedCallbacks([
      () => {
        navigateToGuidanceTooltip(modification);
      },
    ]);
    editor.updateUrl(modification.content.url);
  };

  const onSetGlobal = (
    edit: Modification<GuidanceTooltipSchemaType.guidanceTooltip>,
    isGlobal: boolean
  ) => {
    editor.editGuidanceTooltipModification(edit, isGlobal);
  };

  return (
    <GuidanceManager
      isOpen={isOpen}
      onClose={close}
      guidanceTooltipsModifications={guidanceTooltipsModifications}
      startGuidanceTooltipsMode={startGuidanceTooltipsMode}
      pageUrl={pageUrl}
      deleteGuidanceTooltipModification={deleteGuidanceTooltipModification}
      toggleGuidanceTooltipModificationVisibility={
        toggleGuidanceTooltipModificationVisibility
      }
      onItemHover={(modification) => {
        highlightGuidanceTooltip(modification);
      }}
      onItemUnhover={(modification) => {
        destroyHighlightGuidanceTooltip(modification);
      }}
      onItemClick={handleOnClick}
      onSetGlobal={onSetGlobal}
    />
  );
});

GuidanceTool.displayName = 'GuidanceTool';
